import { Component, Input, OnInit } from '@angular/core';
import * as _ from 'lodash';
import { TrackerService } from '../../services/tracker.service';
import { StateService } from '@uirouter/angular';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TrackerAdminWorkflowTransitionModalComponent } from './trackerWorkflowTransitionModal/tracker.workflow.transition.modal.component';

@Component({
  selector: 'tracker-admin-workflow',
  template: require('./tracker.workflow.component.html')
})
export class TrackerAdminWorkflowComponent implements OnInit {
  @Input() tracker: any;
  @Input() roles: any;
  @Input() extraFields: any;
  @Input() selectFields: any;

  workflowField: any = {};
  workflowMatrixEnabled: boolean = false;
  workflowSortFields: string[] = ['sortOrder', 'label', 'id'];
  fromFieldElement: any;
  toFieldElement: any;
  dropdownOpen: boolean = false;

  /**
   * Constructor for the TrackerAdminWorkflowComponent
   * @param trackerService - Service to handle tracker-related operations
   * @param modalService - Service to handle modal dialogs
   * @param stateService - Service to handle state transitions
   * @param stateParams - UI-Router state parameters
   */
  constructor(
    private trackerService: TrackerService,
    private modalService: NgbModal,
    private stateService: StateService,
  ) {}

  /**
   * Angular lifecycle hook that initializes the component
   */
  ngOnInit() {
    const workflowFieldAlias = this.stateService.params.workflowField;
    const workflowField = _.find(this.tracker.fields, { alias: workflowFieldAlias });
    if (!_.isEmpty(workflowField)) {
      this.selectWorkflowField(workflowField);
    } else {
      this.workflowField = { id: 0, fieldName: 'None', alias: '' };
    }
  }

  /**
   * Private method to set up the workflow matrix
   * @param allDisabled - Flag to disable all elements in the matrix
   */
  private _setWorkflowMatrix(allDisabled: boolean = false) {
    _.forEach(this.workflowField.fieldElements, (fieldElement) => {
      const ret = [];
      _.forEach(this.workflowField.fieldElements, (toElement) => {
        const _toElement = _.find(fieldElement.toElements, { id: toElement.id });
        if (!_toElement) {
          const _disabledToElement: any = this._createToElementObject(toElement);
          _disabledToElement.isEnabled = false;
          ret.push(_disabledToElement);
        } else {
          const _enabledToElement: any = this._createToElementObject(_toElement);
          _enabledToElement.isEnabled = !allDisabled;
          ret.push(_enabledToElement);
        }
      });
      fieldElement.toElements = ret;
    });
  }

  /**
   * Private method to create an object representing a 'toElement'
   * @param toElement - The element to create the object for
   * @returns An object representing the toElement
   */
  private _createToElementObject(toElement: any) {
    if (!_.isEmpty(toElement.changeExtraField) && Array.isArray(toElement.changeExtraField)) {
      toElement.changeExtraField = toElement.changeExtraField[0];
    }

    return {
      id: toElement.id,
      alias: toElement.alias,
      label: toElement.label,
      statusId: toElement.statusId,
      defaultSelected: toElement.defaultSelected,
      sortOrder: toElement.sortOrder,
      allowedRoles: Array.isArray(toElement.allowedRoles) ? toElement.allowedRoles : [],
      notify: {
        message: _.get(toElement, 'notify.message', ''),
        roles: !_.isEmpty(toElement.notify) && Array.isArray(toElement.notify.roles) ? toElement.notify.roles : []
      },
      requiredFields: Array.isArray(toElement.requiredFields) ? toElement.requiredFields : [],
      reassignToRoles: Array.isArray(toElement.reassignToRoles) ? toElement.reassignToRoles : [],
      changeExtraField: {
        id: !_.isEmpty(toElement.changeExtraField) ? toElement.changeExtraField.id : null,
        alias: !_.isEmpty(toElement.changeExtraField) ? toElement.changeExtraField.alias : null,
        fieldName: !_.isEmpty(toElement.changeExtraField) ? toElement.changeExtraField.fieldName : 'None',
        toElementId: !_.isEmpty(toElement.changeExtraField) ? toElement.changeExtraField.toElementId : null,
        toElementName: !_.isEmpty(toElement.changeExtraField) ? toElement.changeExtraField.toElementName : 'None'
      },
      enabledTransitionScripts: Array.isArray(toElement.enabledTransitionScripts) ? toElement.enabledTransitionScripts : null,
      toLockStatus: toElement.toLockStatus,
      requiresApproval: toElement.requiresApproval || false,
      llmPrompt: toElement.llmPrompt,
      // Empty JSON objects round-trip through PHP/json_encode as '[]', which is truthy
      // in JS, so a plain `||` fallback won't catch it — check emptiness explicitly.
      llmOptions: !_.isEmpty(toElement.llmOptions) ? toElement.llmOptions : { postSuccessFollowup: false },
    };
  }

  /**
   * Opens a modal to edit the transition between two field elements
   * @param fromFieldElement - The element the transition starts from
   * @param toFieldElement - The element the transition ends at
   */
  showEditTransition(fromFieldElement: any, toFieldElement: any) {
    this.fromFieldElement = fromFieldElement;
    this.toFieldElement = toFieldElement;

    const modalRef = this.modalService.open(TrackerAdminWorkflowTransitionModalComponent);
    modalRef.componentInstance.tracker = this.tracker;
    modalRef.componentInstance.workflowField = this.workflowField;
    modalRef.componentInstance.fromFieldElement = this.fromFieldElement;
    modalRef.componentInstance.toFieldElement = this.toFieldElement;
    modalRef.componentInstance.roles = this.roles;
    modalRef.componentInstance.extraFields = this.extraFields;
    modalRef.componentInstance.selectFields = this.selectFields;
    modalRef.componentInstance.fieldType = 0;
    modalRef.componentInstance.close.subscribe(() => {
      modalRef.close();
    });
  }

  /**
   * Checks if a workflow field is currently set
   * @returns True if a workflow field is set, false otherwise
   */
  isWorkflowFieldSet(): boolean {
    return !_.isEmpty(this.workflowField) && this.workflowField.alias !== '';
  }

  /**
   * Selects a workflow field to display its workflow matrix
   * @param field - The field to select
   */
  selectWorkflowField(field: any) {
    if (this.isWorkflowSetOnField(field)) {
      this.workflowField = _.cloneDeep(_.find(this.tracker.rel.workflow.fields, { alias: field.alias }));
      this.workflowMatrixEnabled = true;
      this._setWorkflowMatrix();
    } else {
      const _field = {
        alias: field.alias,
        fieldName: field.fieldName,
        fieldElements: []
      };

      field.fieldElements.forEach((element: any) => {
        const _fieldElement = {
          id: element.id,
          alias: element.alias,
          label: element.label,
          toElements: []
        };

        _field.fieldElements.push(_fieldElement);
      });

      this.workflowField = _field;
      this.workflowMatrixEnabled = false;
      this._setWorkflowMatrix(true);
    }
  }

  /**
   * Enables or disables workflow for the selected field
   * @param field - The field to enable or disable workflow for
   * @param enableWorkflow - Flag indicating whether to enable or disable workflow
   */
  setWorkflowForField(field: any, enableWorkflow: boolean) {
    if (enableWorkflow && !this.isWorkflowSetOnField(field)) {
      if (!_.has(this.tracker.rel.workflow, 'fields')) {
        this.tracker.rel.workflow.fields = [];
      }

      this.tracker.rel.workflow.fields[field.alias] = _.cloneDeep(this.tracker.fields[field.alias]);
      this.workflowMatrixEnabled = true;
      this._setWorkflowMatrix(true);

      this.trackerService.workflow.save(this.tracker.id, this.workflowField, true).then(() => {
        const currentTab = _.get(this.stateService, 'params.tab', 'tab-workflow');
        this.stateService.go('.', { workflowField: field.alias, tab: currentTab }, { reload: true, inherit: true, notify: true });
      });
    } else if (!enableWorkflow) {
      this.trackerService.workflow.delete(this.tracker.id, this.workflowField.alias).then(() => {
        const currentTab = _.get(this.stateService, 'params.tab', 'tab-workflow');
        this.stateService.go('.', { workflowField: '', tab: currentTab }, { reload: true, inherit: true, notify: true });
      });
    }
  }

  /**
   * Checks if workflow is already set for a given field
   * @param field - The field to check
   * @returns True if workflow is set for the field, false otherwise
   */
  isWorkflowSetOnField(field: any): boolean {
    return !_.isEmpty(_.find(this.tracker.rel.workflow.fields, { alias: field.alias }));
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  closeDropdown() {
    this.dropdownOpen = false;
  }
}
